import { useCallback, useEffect, useState } from 'react';
import { getCandidateScreeningProgressSummary } from '../../../service/VolunteerApplicationService';
import { useAuth } from '../../../hooks/useAuth';
import { useBatchId } from '../../../hooks/useBatchId';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	CandidateScreeningProgressSummaryFilter,
	CandidateScreeningProgressSummaryResponse,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useSnapshot } from 'valtio';
import { DashboardListState } from './DashboardListStore';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';

export const useScreeningDashboard = () => {
	const { srcmId } = useAuth();
	const { batchId } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const [adminDashboardResponse, setAdminDashboardResponse] =
		useState<CandidateScreeningProgressSummaryResponse>();
	const [dashboardFilter, setDashboardFilter] =
		useState<CandidateScreeningProgressSummaryFilter>({});
	const filterStateSnap = useSnapshot(DashboardListState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		DashboardListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const onDashboardFilterChange = useCallback(
		(filterParam: CandidateScreeningProgressSummaryFilter) => {
			DashboardListState.filterBy = { ...filterParam };
			setDashboardFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const getCandidateScreeningProgressSummaryData = useCallback(
		async (filter: CandidateScreeningProgressSummaryFilter) => {
			if (srcmId && batchId) {
				try {
					const screeningDashboardResponse =
						await getCandidateScreeningProgressSummary({
							functionarySrcmId: srcmId,
							batchId,
							filter,
						});
					if (screeningDashboardResponse) {
						setAdminDashboardResponse(screeningDashboardResponse);
					}
				} catch (err) {
					showToastMessage(
						'error',
						err instanceof Error ? err.message : 'Unknown error occurred'
					);
				}
			}
		},
		[srcmId, batchId, showToastMessage]
	);

	useEffect(() => {
		void getCandidateScreeningProgressSummaryData(dashboardFilter);
	}, [dashboardFilter, getCandidateScreeningProgressSummaryData]);

	return {
		adminDashboardResponse,
		dashboardFilter,
		onDashboardFilterChange,
		paging,
		onPageChange,
	};
};
